import { AxiosError } from 'axios'
import { getCustomSettings } from 'client/actions/customSettings'
import { getExperiments } from 'client/actions/experiments'
import { getAllProducts } from 'client/actions/products'
import { getInvoiceType } from 'client/components/invoiceEditor/InvoiceEditorController'
import { EInvoicingScheme } from 'client/Global/EInvoicingData'
import { PaymentMeans } from 'client/Interfaces/Settings/PaymentMeanInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { ValidationFieldsInterface } from 'client/Interfaces/validationFieldsInterface'
import { filterColumnsValueBasedOnInvoiceType, getSettingsIbans } from 'client/reducers/draft'
import dayjs from 'dayjs'
import { mapValues } from 'lodash'
import moment from 'moment'
import { ActionCreator, EmptyActionCreator } from 'typescript-fsa'

import { getDocument, getDraftDocumentHTML } from '../api/document'
import * as api from '../api/draft'
import * as delivery from '../api/invoiceDelivery'
import { getNextNumber, InvoicenumberResponseInterface } from '../api/invoiceNumber'
import { getValidationFields } from '../api/validationFields'
import { notifier } from '../components/common/Notifier'
import { handleInvoiceConfirmationModal } from '../components/Modal/InvoiceConfirmationModal'
import { PremiumFeatureType } from '../components/Modal/PremiumFeatureIntermediaryModal'
import { Analytics, checkPlacement } from '../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  deliveryType,
  Events,
  Placement,
  PremiumModalOrigin,
  Result,
} from '../Global/AnalyticsEvents'
import { Segment } from '../Global/Segment'
import { SegmentEvents } from '../Global/SegmentEvents'
import { localStorage } from '../globalWindow'
import { refreshDocument } from '../graphQueries/invoices'
import { CompanyInterface } from '../Interfaces/CompanyInterface'
import { ConfigInterface } from '../Interfaces/ConfigsInterface'
import { CustomerInterface } from '../Interfaces/CustomerInterface'
import {
  CharacterLimitInterface,
  DocumentNumberType,
  DocumentType,
  DocumentTypes,
  DraftInterface,
  InvoicerInterface,
} from '../Interfaces/Draft/DraftInterface'
import { DraftFieldsInterface, paymentReferenceTypes } from '../Interfaces/Draft/FieldsInterface'
import { defFields, LineInterface } from '../Interfaces/Draft/LineInterface'
import { ExternalTaxRatesInterface } from '../Interfaces/ExternalVATRatesInterface'
import { InvoiceInterface } from '../Interfaces/Invoice/InvoiceInterface'
import { InvoiceTypeInterface } from '../Interfaces/InvoiceTypeInterface'
import { OrderDraftInterface } from '../Interfaces/OrderInterface'
import { ProductInterface } from '../Interfaces/ProductInterface'
import { RecurringProfileInterface } from '../Interfaces/RecurringProfileInterface'
import { SettingsInterface } from '../Interfaces/Settings/SettingsInterface'
import { TradeNameInterface } from '../Interfaces/Settings/TradeNameInterface'
import { QuotaTypes } from '../Interfaces/Subscription'
import { TokenInterface } from '../Interfaces/TokenInterface'
import { getInvoiceTypes, getLocale } from '../Other/Configs'
import { calculateDaysFromStartDate } from '../Other/functions/dateFunctions'
import { downloadDocumentPDF, downloadXRechnung } from '../Other/functions/documentFunctions'
import { discardExtraTotals, getFieldDefaultValue, handleFieldsBeforeSave } from '../Other/functions/modelFunctions'
import { generateReferenceNumber, generateStructuredCommunication } from '../Other/functions/referenceNumberFunctions'
import { Immutable } from '../Other/Immutable'
import { Type as InvoiceType } from '../Other/Type'
import {
  doesCurrentPlanSupportFeature,
  getCompanySubscriptionSelector,
} from '../reducers/Subscription/companySubscription'
import { actionCreator, actionCreatorAsync } from './'
import { DraftActions, InvoiceActions } from './actionTypes'
import { getExternalVATRates } from './agerasExternal'
import { getCompany } from './company'
import { getCustomers } from './customer'
import * as DocumentNumberActions from './documentNumber'
import { updateEstimateAndClose } from './estimate'
import {
  createEstimateDraft,
  editDraft as editEstimateDraft,
  previewDraft as previewEstimateDraft,
  updateEstimateDraft,
} from './estimateDraft'
import { getExchangeRate } from './exchangeRate'
import { getCustomerName } from './helpers/customerName'
import { defaultDocument, hasMinimumInfo, isInvoiceNumberNewSequence } from './helpers/draft'
import { getDraftsAndInvoices, getInvoiceCount } from './helpers/queries'
import { getDefaultRecurringProfile, isRecurring } from './helpers/recurringProfile'
import { AsyncAction } from './index'
import { updateInvoiceAndClose } from './invoice'
import {
  loadingEditorVitals,
  navigateAddModal,
  navigateRemoveModal,
  setNavigation,
  setNavigationPath,
} from './navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from './ongoing'
import { createRecurringProfile, updateRecurringProfile, updateRecurringProfileSettings } from './recurringProfile'
import { getSettings } from './settings'

export const assignReceiver: ActionCreator<CustomerInterface> = actionCreator<CustomerInterface>('ASSIGN_RECEIVER')
export const clearDraft: EmptyActionCreator = actionCreator('CLEAR_DRAFT')

export const PDF_SPINNER = 'PDF_SPINNER'

export interface AddProductInterface {
  index: number
  product: ProductInterface
}

export interface ChangeDraftInterface {
  draft: DraftInterface
  tradeNames: TradeNameInterface[]
  externalVATRates?: ExternalTaxRatesInterface
  invoiceType: InvoiceTypeInterface
}

export interface ChangeFieldsInterface {
  fields: DraftFieldsInterface
  settings: SettingsInterface
  configs: ConfigInterface
  country: string
  externalVATRates?: ExternalTaxRatesInterface
}

const handleTemplateName = (templateName: string, country: string): string => {
  let template = templateName

  if (!template.includes(`${country}`)) {
    template = `${country}.${template}`
  }
  if (['FI', 'SE', 'AX'].includes(country) && !template.includes('OneP')) {
    template = `${template}OneP`
  }
  return template
}

const trackVatRateErrorApprovalAttempts = (draft) => {
  const approvalAttempts = JSON.parse(localStorage.getItem('approvalAttempts') || '{"vatRateError":{}}')
  const documentId = draft.id as number
  const previousAttemptNumber = approvalAttempts.vatRateError?.[documentId] || 0
  const attemptNumber = previousAttemptNumber + 1
  Segment.track(SegmentEvents.accounting.invoiceApprovalFailedVatRateError, {
    invoice_id: documentId,
    attempt_number: attemptNumber,
  })
  approvalAttempts.vatRateError[documentId] = attemptNumber
  localStorage.setItem('approvalAttempts', JSON.stringify(approvalAttempts))
}

export const updateOrder = actionCreator<OrderDraftInterface>(DraftActions.UPDATE_ORDER)
export const deleteOrder = actionCreator(DraftActions.DELETE_ORDER)

export const changeInvoicerVatNumber = actionCreator<string>('DRAFT_CHANGE_VAT_NUMBER')

const changeLinesAction = actionCreator<LineInterface[]>('CHANGE_LINES')
export const changeLines = (lines: LineInterface[]) => {
  return (dispatch, getState) => {
    dispatch(changeLinesAction(lines))
    dispatch(exportedActions.changeDraft(getState().draft))
  }
}

const changeDraftAction = actionCreator<ChangeDraftInterface>('CHANGE_DRAFT')
export const changeDraft = (draft: DraftInterface) => {
  return (dispatch, getState) => {
    const { config, settings } = getState()
    const tradeNames: TradeNameInterface[] = settings.values.company.tradeNames
    const country = settings.values.company.country
    if (tradeNames) {
      for (const tradeName of tradeNames) {
        tradeName.country = country
      }
    }
    dispatch(
      changeDraftAction({
        draft,
        tradeNames,
        externalVATRates: getState().externalVATRates,
        invoiceType: draft?.fields?.type.value && config.invoiceTypes[draft?.fields?.type.value],
      }),
    )
  }
}

export const updateDraftInvoiceTypeSettingsFromSettings = () => {
  return (dispatch, getState) => {
    const { draft, settings } = getState()
    const fields = Immutable.copy(draft.fields)
    const invoiceModel = settings.models.find(({ type }) => type === DocumentTypes.INVOICE)
    fields.type.value = Number(invoiceModel.fields.find(({ name }) => name === 'type').default)
    dispatch(changeFields(fields))
  }
}

const changeFieldsAction = actionCreator<ChangeFieldsInterface>('CHANGE_FIELDS')
export const changeFields = (fields: Partial<DraftFieldsInterface>) => {
  return (dispatch, getState) => {
    const state = getState()
    const { settings, config, draft, externalVATRates } = state

    dispatch(changeFieldsAction({ fields, settings, configs: config, country: getCountry(state), externalVATRates }))
    dispatch(exportedActions.changeLines(getState().draft.lines))
    dispatch(exportedActions.changeDraft(getState().draft))

    if (fields?.dueDate && draft.order?.orderType.startsWith('downPayment')) {
      const newOrder = { ...getState().draft.order }
      newOrder.items[1].dueDate = fields.dueDate.value
      dispatch(updateOrder(newOrder))
    }
  }
}

const addProductLineAction = actionCreator<AddProductInterface>('ADD_PRODUCT_LINE')
export const addProductLine = (payload: AddProductInterface) => {
  return async (dispatch, getState) => {
    dispatch(addProductLineAction(payload))
    if (getState().draft && getState().draft.lines) {
      dispatch(changeLines(getState().draft.lines))
    }
    await dispatch(getSettings(null, true))
  }
}

export const setTypeForInvoiceDraft = (draft: DraftInterface, config: ConfigInterface, country: string) => {
  const invoiceType: InvoiceTypeInterface = getInvoiceTypes(config, country, DocumentTypes.INVOICE, false).filter(
    (t) => {
      return parseInt(t.id, 10) === draft.fields.type.value
    },
  )[0]
  draft.fields.typeName = { name: 'typeName', value: invoiceType.type }
}

const handleFields = (draft: DraftInterface, state: StateInterface) => {
  draft = handleFieldsBeforeSave(draft, state.settings)
  if (draft.document === DocumentTypes.INVOICE) {
    const iban = draft.paymentMeans.find(({ type }) => type === 'IBAN')
    const settingsDefaultIBAN = state.settings.values.paymentMethods.find(({ type }) => type === 'IBAN')
    if (iban && settingsDefaultIBAN) {
      const settingsIBANs = getSettingsIbans(state.settings)
      const matchingIbanFromSettings = settingsIBANs.find((sIBAN) => iban.fields && sIBAN.iban === iban.fields.iban)
      if (!matchingIbanFromSettings) {
        const IBANIndex = draft.paymentMeans.findIndex(({ type }) => type === 'IBAN')
        if (IBANIndex >= 0) {
          iban.fields = settingsDefaultIBAN.fields
        } else {
          draft.paymentMeans.push(settingsDefaultIBAN)
        }
      }
    }
  }

  // for reasons unknown, we might sometimes get a null value for some paymentMean fields,
  // so we need to ensure not to pass it to the backend, as it will break the app
  draft.paymentMeans = draft.paymentMeans?.map((paymentMean) =>
    paymentMean.type !== 'IBAN'
      ? paymentMean
      : {
          ...paymentMean,
          fields: mapValues(paymentMean.fields, (field) => (field === null ? '' : field)),
        },
  )

  delete draft.toBeApproved
  delete draft.fields.invoiceNumber
  delete draft.isDirty
  return draft
}

const saveInvoiceDraft = (draft: DraftInterface, showSpinner = true) => {
  if (draft.id === 0) {
    delete draft.id
  }
  return async (dispatch, getState) => {
    if (draft.id) {
      await dispatch(updateInvoiceDraft(draft))
    } else {
      if (isInvoiceNumberNewSequence(draft, getState().invoiceNumbers)) {
        const { prefix, number: base } = draft.invoiceNumber
        await dispatch(DocumentNumberActions.createInvoiceNumberSequence({ prefix, base: Number(base) }))
      }
      draft = handleFields(draft, getState())
      setTypeForInvoiceDraft(draft, getState().config, getCountry(getState()))
      if (showSpinner) {
        dispatch(showGeneratingDocumentSpinner())
      }
      await dispatch(
        actionCreatorAsync(
          DraftActions.CREATE_INVOICE_DRAFT,
          async () => {
            const invoiceCount = await getInvoiceCount()
            const createdId = await api.createDraft(draft.document, draft, invoiceCount)
            return await api.getDraft(draft.document, createdId)
          },
          (error: AxiosError) => {
            dispatch(hideGeneratingDocumentSpinner())
            if (error?.response?.status === 500) {
              notifier('apix.ApixInvoiceValidationError', 'warning')
            }
            if (error?.response?.status === 422) {
              const isNegativeGrossTotalError = error?.response?.data?.message?.includes('negative grossTotal')
              const errorTranslationKey = isNegativeGrossTotalError ? 'invoice.error_message.negative_sum' : ''
              notifier(errorTranslationKey, 'warning')
            }
            throw error
          },
        ),
      )
      if (showSpinner) {
        dispatch(hideGeneratingDocumentSpinner())
      }
    }
  }
}

export const getNavigationToList = (getState, draft) => {
  const type = draft.document
  const { draft: stateDraft } = getState()
  const isEditInvoice = Boolean(stateDraft?.editInvoice) || false
  const documentId = isEditInvoice ? draft?.id : getState()?.draft?.id
  const reactPath =
    type === 'estimate' ? `/reactEstimateList/estimate/${documentId}` : `/reactInvoiceList/invoice/${documentId}`
  const docPath = module ? reactPath : 'invoices'
  const draftReactPath =
    type === 'estimate'
      ? `/reactEstimateList/estimateDraft/${documentId}`
      : `/reactInvoiceList/invoiceDraft/${documentId}`
  const draftPath = module ? draftReactPath : '/invoices'
  // eslint-disable-next-line no-nested-ternary
  let path = isRecurring(draft) ? '/reactRecurringProfiles' : isEditInvoice ? docPath : draftPath
  if (isRecurring(draft)) {
    const profileId = getState().recurringProfile.id
    path = profileId ? `/reactRecurringProfiles/${profileId}` : '/reactRecurringProfiles'
  }
  const navigateParams: { [k: string]: any } = {
    path,
    app: {
      mod: 'invoices',
      draftType: `${type}s`,
      'tabs-invoices': 'drafts',
      invoiceType: type,
    },
  }
  if (!isEditInvoice) {
    navigateParams.app.draft = getState().draft.id
  } else {
    navigateParams.app['tabs-invoices'] = `${type}s`
  }
  return navigateParams
}

export const navigateBackToList = async (dispatch, getState, draft) => {
  const draftCount = await getDraftsAndInvoices()
  const isEditInvoice = Boolean(draft.editInvoice) || false
  const navigateParams = getNavigationToList(getState, draft)
  dispatch(setNavigation(navigateParams))
  if (!isEditInvoice && !draft.id && draftCount === 0 && !isRecurring(draft)) {
    dispatch(
      navigateAddModal({
        component: 'SuccessModal',
        props: {
          type: 'firstDraft',
        },
      }),
    )
  }
}

const draftSaveAndCloseThunk = (origDraft: DraftInterface, navigateToTheList = true) => {
  return async (dispatch, getState) => {
    const isEditInvoice = Boolean(origDraft.editInvoice) || false
    // getDraftForPreview gets updated draft with correct IBAN
    const draft = getDraftForPreview(origDraft, getState().settings)
    let action
    const type = draft.document
    if (isEditInvoice !== true) {
      // DRAFT_SAVE
      if (type === 'invoice') {
        if (isRecurring(origDraft)) {
          dispatch(async (dispatch, getState) => {
            const profileSettings = getState().recurringProfile
            const profile: RecurringProfileInterface = {
              draft,
              schedule: profileSettings,
            }
            setTypeForInvoiceDraft(draft, getState().config, getCountry(getState()))
            if (!profileSettings.id) {
              action = createRecurringProfile(profile)
              Analytics.trackEvent(Events.saveRecurringInvoice, {
                type: AnalyticsType.action,
                category: Category.recurringInvoice,
                result: Result.success,
                placement: checkPlacement(window.location.pathname),
                schedule: {
                  id: profile.schedule.id,
                  frequency: profile.schedule.frequency,
                  action: profile.schedule.action,
                  duration: profile.schedule.repeat ? 'limited' : 'indefinite',
                  repeat: profile.schedule.repeat,
                  notify: profile.schedule.confirmation,
                },
              })
            } else {
              profile.id = profileSettings.id
              action = updateRecurringProfile(profile)
              Analytics.trackEvent(Events.editRecurringInvoice, {
                type: AnalyticsType.action,
                category: Category.recurringInvoice,
                result: Result.success,
                placement: checkPlacement(window.location.pathname),
                schedule: {
                  id: profile.schedule.id,
                  frequency: profile.schedule.frequency,
                  action: profile.schedule.action,
                  duration: profile.schedule.repeat ? 'limited' : 'indefinite',
                  repeat: profile.schedule.repeat,
                  notify: profile.schedule.confirmation,
                },
              })
            }
            await dispatch(action)
          })
        } else {
          action = draft.id === 0 ? exportedActions.saveInvoiceDraft(draft) : updateInvoiceDraft(draft)
          await dispatch(action)
        }
      } else if (type === 'estimate') {
        action = draft.id === 0 ? createEstimateDraft(draft) : updateEstimateDraft(draft)
        await dispatch(action)
      }
    } else {
      if (type === 'estimate') {
        await dispatch(updateEstimateAndClose(draft))
      } else {
        await dispatch(updateInvoiceAndClose(draft))
      }
    }
    if (navigateToTheList) {
      // NAVIGATE
      dispatch(async (dispatch, getState) => {
        return navigateBackToList(dispatch, getState, draft)
      })
    }
  }
}

export const draftSaveAndClose = async (origDraft: DraftInterface, dispatch: any, navigateToTheList = true) => {
  dispatch(draftSaveAndCloseThunk(origDraft, navigateToTheList))
}

export const createInvoiceDraftAction = actionCreator<DraftInterface>(DraftActions.CREATE_INVOICE_DRAFT)
export const updateInvoiceDraftAction = actionCreator<DraftInterface>(DraftActions.UPDATE_INVOICE_DRAFT)

export const updateInvoiceDraft = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    draft = handleFields(draft, getState())
    setTypeForInvoiceDraft(draft, getState().config, getCountry(getState()))
    await dispatch(
      actionCreatorAsync(
        DraftActions.UPDATE_INVOICE_DRAFT,
        async () => {
          dispatch(showGeneratingDocumentSpinner())
          const updatedDraft = await api.updateDraft(DocumentTypes.INVOICE, draft)
          dispatch(hideGeneratingDocumentSpinner())
          return updatedDraft
        },
        (error: AxiosError) => {
          if (error?.response?.status === 500) {
            dispatch(hideGeneratingDocumentSpinner())
            notifier('apix.ApixInvoiceValidationError', 'warning')
          }
          throw error
        },
      ),
    )
  }
}

export function cleanDraftForApproval(draft: DraftInterface): DraftInterface {
  let cleanDraft = Immutable.copy(draft)
  cleanDraft = discardExtraTotals(cleanDraft)
  delete cleanDraft.invalidFields
  delete cleanDraft.preview
  delete cleanDraft.legacyInvoiceId
  return cleanDraft
}

export const approveInvoiceDraft = (draft: DraftInterface, isAgerasNextMigration?: Boolean) => {
  return async (dispatch): Promise<InvoiceInterface[]> => {
    try {
      if (isAgerasNextMigration && !draft?.receiver?.externalContactId) {
        Segment.track(SegmentEvents.invoice.externalContactIdMissing)
      }
      const cleanDraft = cleanDraftForApproval(draft)
      const invoiceCount = await getInvoiceCount()
      let documentIds
      let firstInvoice = false
      if (draft.document === DocumentTypes.INVOICE) {
        firstInvoice = invoiceCount === 0
        let approved: { id: number }[] = []
        try {
          approved = await api.approveInvoiceDraft(cleanDraft, invoiceCount)
        } catch (error) {
          if (error.response?.status === 500 && String(error.response?.data?.message).includes('locked until')) {
            Segment.track(SegmentEvents.draft.lockDateError, {
              invoice_id: draft.id ?? 'NULL',
              reason: error.response?.data?.message,
            })
          }
          throw error
        }
        documentIds = approved.map((invoice) => invoice.id)
      } else {
        const approved = await api.approveNonInvoiceDraft(draft.document, cleanDraft)
        documentIds = [approved.id]
      }
      const invoices: any = await Promise.all(documentIds.map((id) => getDocument(draft.document, id)))
      await dispatch(
        actionCreatorAsync(InvoiceActions.SET_INVOICE, () => Promise.resolve({ ...invoices[0], firstInvoice })),
      )
      return invoices
    } catch (error) {
      await dispatch(navigateRemoveModal('InvoiceConfirmationModal'))
      await dispatch(hideGeneratingDocumentSpinner())
      await dispatch(setNavigationPath('/reactInvoice'))
      if (error.status === 400) {
        notifier('apix.ApixInvoiceValidationError', 'warning')
      } else {
        // if vat rate id is a predefined tag, it means the vat rate ids were not synced yet,
        // thus the error when approving the invoice
        const vatRatesNotSynced = draft?.lines?.[0]?.vatRateId?.startsWith('s_fr_')
        if (vatRatesNotSynced) {
          trackVatRateErrorApprovalAttempts(draft)
        }

        notifier('apix.ApixInvoiceApproveError', 'warning')
      }
      throw error
    }
  }
}

export const getDefaultBuyerReference = (draft: DraftInterface): string => {
  const eInvoiceAddress = draft?.receiver?.eInvoiceAddress
  let defaultBuyerReference = ''
  if (eInvoiceAddress?.scheme === EInvoicingScheme.LWID) {
    defaultBuyerReference = eInvoiceAddress?.address || ''
  }
  return defaultBuyerReference
}

export const tryApproveDraftAndDownloadXRechnung = (draft: DraftInterface) => {
  return async (dispatch) => {
    const receiverHasLeitwegId =
      draft?.receiver?.eInvoiceAddress?.scheme === EInvoicingScheme.LWID &&
      !!draft?.receiver?.eInvoiceAddress?.address?.trim()
    if (!draft?.fields?.buyerReference?.value || receiverHasLeitwegId) {
      dispatch(
        navigateAddModal({
          component: 'DownloadXRechnungModal',
          props: {
            defaultBuyerReference: getDefaultBuyerReference(draft),
            onCancel: () => {
              dispatch(navigateRemoveModal('DownloadXRechnungModal'))
            },
            onSave: (buyerReference) => {
              if (draft?.fields) {
                draft.fields.buyerReference = { name: 'buyerReference', value: buyerReference, visible: true }
              }
              dispatch(navigateRemoveModal('DownloadXRechnungModal'))
              handleInvoiceConfirmationModal({
                dispatch,
                onApproveInvoice: () => {
                  dispatch(approveDraftAndDownloadXRechnung(draft))
                },
              })
            },
          },
        }),
      )
    } else {
      dispatch(approveDraftAndDownloadXRechnung(draft))
    }
  }
}

export const approveDraftAndDownloadXRechnung = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    draft.toBeApproved = true
    await dispatch(exportedActions.saveInvoiceDraft(draft))
    dispatch(showGeneratingDocumentSpinner())
    const invoices = await exportedActions.dispatchAndApproveInvoiceDraft(dispatch, getState)
    dispatch(hideGeneratingDocumentSpinner())
    invoices.map((invoice) => downloadXRechnung(invoice))
    const selectInvoice = invoices[0]
    const { settings, navigation } = getState()
    const docType = selectInvoice.creditnotedocument ? 'creditNote' : 'invoice'
    const module = settings.modules.find((mod) => mod.name === 'reactInvoiceList' && !mod.hidden)
    const mod = module ? 'reactInvoiceList' : 'invoices'
    const path = module ? `/${mod}/${docType}/${selectInvoice.id}` : `/${mod}`
    const updatedNavigation = {
      path,
      app: {
        ...navigation.app,
        mod,
        'tabs-invoices': 'invoices',
        invoice: selectInvoice.invoice_id,
      },
    }

    await dispatch(setNavigation(updatedNavigation))
  }
}

export const approveDraftAndDownloadPdf = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    draft.toBeApproved = true
    await dispatch(exportedActions?.saveInvoiceDraft(draft))
    dispatch(showGeneratingDocumentSpinner())
    const invoices = await exportedActions?.dispatchAndApproveInvoiceDraft(dispatch, getState)
    dispatch(hideGeneratingDocumentSpinner())
    invoices.map((invoice) => downloadDocumentPDF(invoice))
    const selectInvoice = invoices[0]
    const { settings, navigation } = getState()
    const docType = selectInvoice?.creditnotedocument ? 'creditNote' : 'invoice'
    const module = settings?.modules?.find((mod) => mod?.name === 'reactInvoiceList' && !mod?.hidden)
    const mod = module ? 'reactInvoiceList' : 'invoices'
    const path = module ? `/${mod}/${docType}/${selectInvoice?.id}` : `/${mod}`
    const updatedNavigation = {
      path,
      app: {
        ...navigation.app,
        mod,
        'tabs-invoices': 'invoices',
        invoice: selectInvoice?.invoice_id,
      },
    }

    await dispatch(setNavigation(updatedNavigation))
  }
}

export const getCountry = (state: StateInterface) => {
  return state.company ? state.company.country : ''
}

export const validationError: ActionCreator<string[]> = actionCreator<string[]>('DRAFT_VALIDATION_ERROR')
export const updateValidationError: ActionCreator<string[]> = actionCreator<string[]>('UPDATE_DRAFT_VALIDATION_ERROR')
export const updateInvoicerAction = actionCreator<InvoicerInterface>('UPDATE_INVOICER')
export const updateInvoiceNumber: ActionCreator<DocumentNumberType> =
  actionCreator<DocumentNumberType>('UPDATE_INVOICE_NUMBER')
export const updateEstimateNumber: ActionCreator<InvoicenumberResponseInterface> =
  actionCreator<InvoicenumberResponseInterface>(DraftActions.UPDATE_ESTIMATE_NUMBER)
export const updateReferenceNumber: ActionCreator<string> = actionCreator<string>('UPDATE_REFERENCE_NUMBER')
export const updateEInvoiceSending: ActionCreator<boolean> = actionCreator<boolean>('UPDATE_EINVOICE_SENDING')
export const updateCharacterLimit: ActionCreator<CharacterLimitInterface> =
  actionCreator<CharacterLimitInterface>('UPDATE_CHARACTER_LIMIT')

export const sendPaperInvoice = async (invoice, invoiceId) => {
  const result = await delivery.sendPaperInvoice(invoiceId)
  if (!result.success) {
    notifier('apix.ApixSendError', 'warning')
  } else {
    await refreshDocument(invoiceId, DocumentTypes.INVOICE)
    Analytics.trackEvent(Events.sendDocument, {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.success,
      placement: checkPlacement(window.location.pathname),
      documentType: AnalyticsObject.invoice,
      documentID: invoiceId,
      testInvoice: invoice.document === DocumentTypes.INVOICE && invoice.invoiceNumber.prefix === 'TEST',
      deliveryType: deliveryType.paper,
      orderData: invoice.order
        ? {
            projectName: invoice.order.name,
            orderType: invoice.order.orderType,
            orderItemsLength: invoice.order.items.length,
            scheduleId: invoice.order.scheduleId,
          }
        : null,
      onlinePayments: null,
    })
    Segment.track(SegmentEvents.invoice.sent, { invoice_id: invoiceId, sending_method: 'post' })
    notifier('notifications.invoice.send.success', 'success')
  }
}

export const sendEInvoice = async (
  invoice,
  attachments,
  isPublicEntity,
  documentType: DocumentTypes = DocumentTypes.INVOICE,
) => {
  const result = await delivery.sendEInvoice(invoice.id, attachments, isPublicEntity, documentType)
  if (!result.success) {
    notifier('apix.ApixSendError', 'warning')
  } else {
    const document = documentType === DocumentTypes.INVOICE ? invoice.invoicedocument : invoice.creditnotedocument
    Analytics.trackEvent(Events.sendDocument, {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.success,
      placement: checkPlacement(window.location.pathname),
      documentType: AnalyticsObject.invoice,
      documentID: invoice.invoice_id,
      testInvoice: documentType === DocumentTypes.INVOICE && document.invoiceNumber.prefix === 'TEST',
      deliveryType: deliveryType.eInvoice,
      orderData: document.order
        ? {
            projectName: document.order.name,
            orderType: document.order.orderType,
            orderItemsLength: document.order.items.length,
            scheduleId: document.order.scheduleId,
          }
        : null,
      onlinePayments: null,
    })
    if (documentType === DocumentTypes.INVOICE) {
      Segment.track(SegmentEvents.invoice.sent, { invoice_id: invoice.id, sending_method: 'einvoice' })
    }
    notifier('notifications.invoice.send.success', 'success')
  }
}

export const dispatchAndApproveInvoiceDraft = async (dispatch, getState) => {
  const { draft, company } = getState()
  return dispatch(approveInvoiceDraft(draft, company?.isAgerasNextMigration))
}

const exportedActions = {
  saveInvoiceDraft,
  dispatchAndApproveInvoiceDraft,
  changeDraft,
  changeLines,
}

export default exportedActions

const saveApproveDraftAndPreparePDF = async (dispatch, getState, draft: DraftInterface) => {
  draft.toBeApproved = true
  await dispatch(exportedActions.saveInvoiceDraft(draft))
  dispatch(showGeneratingDocumentSpinner())
  await exportedActions.dispatchAndApproveInvoiceDraft(dispatch, getState)
  dispatch(hideGeneratingDocumentSpinner())
}

export const navigateAfterSending = async (dispatch, getState) => {
  const module = getState().settings.modules.find((mod) => mod.name === 'reactInvoiceList' && !mod.hidden)
  const docType = getState().invoice.creditnotedocument ? 'creditNote' : 'invoice'
  await dispatch(
    setNavigation({
      path: module ? `/reactInvoiceList/${docType}/${getState().invoice.id}` : '/invoices',
      app: {
        ...getState().navigation.app,
        mod: 'invoices',
        'tabs-invoices': 'invoices',
        invoice: getState().invoice.invoice_id,
      },
    }),
  )
}

export const approveDraftWithoutSending = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    await saveApproveDraftAndPreparePDF(dispatch, getState, draft)
    await navigateAfterSending(dispatch, getState)
  }
}

export const approveDraftAndSendPaper = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    await saveApproveDraftAndPreparePDF(dispatch, getState, draft)
    await dispatch(navigateRemoveModal('SendPaperInvoiceModal'))
    await navigateAfterSending(dispatch, getState)
    await sendPaperInvoice(draft, getState().invoice.id)
  }
}

export const approveDraftAndSendEInvoice = (draft: DraftInterface, attachmentsUUIDs: string[], isPublicEntity) => {
  return async (dispatch, getState) => {
    await saveApproveDraftAndPreparePDF(dispatch, getState, draft)
    await dispatch(navigateRemoveModal('SendEInvoiceModal'))
    await navigateAfterSending(dispatch, getState)
    await sendEInvoice(getState().invoice, attachmentsUUIDs, isPublicEntity, draft.document)
  }
}

export const updateDraftCompanyFromSettings = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_DRAFT_COMPANY_FROM_SETTINGS,
)

export const updateDraftFieldsFromSettings = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_DRAFT_FIELDS_FROM_SETTINGS,
)

export const updatePaymentReferenceFromSettings = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_PAYMENT_REFERENCE_TYPE_FROM_SETTINGS,
)

export const updateCustomerNumberFromSettings = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_CUSTOMER_NUMBER_FROM_SETTINGS,
)

export const updateDraftStylesFromSettings = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_DRAFT_STYLES_FROM_SETTINGS,
)

export const updateDraftCompanyFromCompany = actionCreator<CompanyInterface>(
  DraftActions.UPDATE_DRAFT_COMPANY_FROM_COMPANY,
)

export const replacePaymentMethodsFromSettings = actionCreator<SettingsInterface>(
  DraftActions.REPLACE_PAYMENT_METHODS_FROM_SETTINGS,
)
export const updateDraftCustomerFromCustomers = actionCreator<SettingsInterface>(
  DraftActions.UPDATE_DRAFT_CUSTOMER_FROM_CUSTOMERS,
)

export const updateInvoiceColumnsFromInvoiceType = actionCreator<ConfigInterface>(
  DraftActions.UPDATE_INVOICE_COLUMNS_FROM_INVOICE_TYPE,
)

export const updateDraftCustomerAction = actionCreator<CustomerInterface>(DraftActions.UPDATE_DRAFT_CUSTOMER)

export const getValidationFieldsAction = actionCreator<ValidationFieldsInterface>(DraftActions.GET_VALIDATION_FIELDS)

const createFieldFromData = (name, data) => ({
  name,
  visible: false,
  ...(data || {}),
})

export const updateDraftCustomer = (customer: CustomerInterface, isEdit = false) => {
  return async (dispatch, getState) => {
    const {
      draft: { fields },
      config,
      products,
      externalVATRates,
    } = getState() as StateInterface
    const hasCustomer = Boolean(customer && customer.language)
    const cusPaymentTime = hasCustomer && customer.paymentTerm
    const oldPaymentTime = fields?.paymentTime && fields?.paymentTime.value
    const useIfNumber = (v: any, d: number): number => (typeof v === 'number' ? v : d)
    const defaultPaymentTime = 30

    const localeData =
      !isEdit && hasCustomer
        ? {
            locale: {
              ...createFieldFromData('locale', fields?.locale),
              value: getLocale(config, customer.language, customer.address.country || customer.language),
            },
          }
        : {}
    const paymentTimeData = {
      paymentTime: {
        ...createFieldFromData('paymentTime', fields.paymentTime),
        value: isEdit
          ? useIfNumber(oldPaymentTime, defaultPaymentTime)
          : useIfNumber(cusPaymentTime, defaultPaymentTime),
      },
    }
    const customerData = {
      customer: {
        ...createFieldFromData('customer', fields.customer),
        value: (hasCustomer && `${customer.id}`) || null,
      },
    }

    const updatedFields: Partial<DraftFieldsInterface> = {
      ...localeData,
      ...paymentTimeData,
      ...customerData,
    }
    await dispatch(updateDraftCustomerAction(hasCustomer ? customer : null))
    if (window.initialConfiguration.eInvoiceOnboardingEnabled) {
      dispatch(updateValidationField(customer))
    }

    dispatch(
      changeFields({
        ...getState().draft.fields,
        ...updatedFields,
      }),
    )

    /**
     * When changing customer, update the columns based on the invoice type
     */
    const stateAfterFieldChanges = getState() as StateInterface
    const columnData: Partial<DraftFieldsInterface> = {
      columns: {
        ...createFieldFromData('columns', stateAfterFieldChanges.draft.fields?.columns),
        value: filterColumnsValueBasedOnInvoiceType({
          columnsValue: stateAfterFieldChanges.draft.fields?.columns.value ?? [],
          invoiceType: getInvoiceType(stateAfterFieldChanges),
        }),
      },
    }
    dispatch(
      changeFields({
        ...stateAfterFieldChanges.draft.fields,
        ...columnData,
      }),
    )
    const newColumns = (getState() as StateInterface)?.draft?.fields?.columns.value
    if (new Set(newColumns).has('vatPct')) {
      const newLines: LineInterface[] = ((getState() as StateInterface)?.draft?.lines ?? []).map(
        (line: LineInterface) => {
          if (line.type === 'row' && !line.vatPct) {
            const vatPct = products?.find((p) => p.name === line.description)?.vatPct || 0
            return {
              ...line,
              vatPct,
              vatRateId: externalVATRates?.taxRates.find((r) => r.rate === vatPct)?.id,
            }
          } else {
            return line
          }
        },
      )
      dispatch(exportedActions.changeLines(newLines))
    }
  }
}

export const updateValidationField = (customer: CustomerInterface) => {
  return async (dispatch, getState) => {
    const {
      draft: { fields },
    } = getState() as StateInterface
    const hasCustomer = Boolean(customer && customer.language)
    const eInvoiceAddress = {
      address: hasCustomer ? customer.eInvoiceAddress.address : null,
      scheme: hasCustomer ? customer.eInvoiceAddress.scheme : null,
    }
    const validationFields = await getValidationFields(eInvoiceAddress.address, eInvoiceAddress.scheme)
    await dispatch(getValidationFieldsAction(validationFields))
    if (validationFields.required?.includes('recipientReference') && !fields.recipientReference.visible) {
      dispatch(
        changeFields({
          ...fields,
          recipientReference: { visible: true, value: '', name: 'recipientReference' },
        }),
      )
    }
  }
}

export const loadVitalsForEditor = async (dispatch, state: StateInterface) => {
  const hasExchangeRate = Boolean(state.exchangeRate)
  // fetch exchange rate if not already loaded
  if (!hasExchangeRate) {
    await dispatch(getExchangeRate())
  }

  dispatch(getCompany())

  if (!state.customers?.collection) {
    await dispatch(getCustomers())
  }

  if (!state.products?.length) {
    await dispatch(getAllProducts())
  }

  if (!state.externalVATRates?.taxRates?.length && state.company?.agerasNextMigration) {
    await dispatch(getExternalVATRates())
  }
}

export function loadDraft(id: number): AsyncAction<DraftInterface> {
  return actionCreatorAsync<DraftInterface>(DraftActions.LOAD_DRAFT, async () =>
    api.getDraft(DocumentTypes.INVOICE, id),
  )
}

// Can be removed together with the old invoice list
export const editDraftFromLegacy = (id: number, type: DocumentType) => {
  return async (dispatch) => {
    if (type === DocumentTypes.INVOICE) {
      await dispatch(editDraft(id))
    } else if (type === DocumentTypes.ESTIMATE) {
      await dispatch(editEstimateDraft(id))
    }
  }
}

export const editDraft = (id: number, trackerEntriesToInvoice = false) => {
  Analytics.trackEvent(`${Events.openDocument}`, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: Placement.invoices,
    documentType: AnalyticsObject.invoice,
    draftID: id,
  })

  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async () => {
        await loadVitalsForEditor(dispatch, getState())
        await dispatch(loadDraft(id))
        await dispatch(DocumentNumberActions.getInvoiceNumberSequences())
        await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
        await dispatch(getCustomers(false))
        const state = getState()
        const draftReceiver = state.draft.receiver
        const allCustomers = Immutable.copy(state.customers.collection)
        const updatedCustomer = allCustomers.find((c) => c?.id === draftReceiver?.id) || draftReceiver

        // Change the invoice template to users default only when transferring time tracker entries
        if (trackerEntriesToInvoice) {
          const country = state.token.country
          const defaultTemplate = getFieldDefaultValue('template', state.settings, 'invoice') as string
          const template = handleTemplateName(defaultTemplate, country)
          state.draft.fields.template.value = template
        }

        dispatch(updateDraftFieldsFromSettings(state.settings))
        dispatch(updateDraftCustomerAction(updatedCustomer))
        dispatch(updateDraftCompanyFromSettings(state.settings))
        dispatch(updateDraftCompanyFromCompany(state.company))
        dispatch(updateDraftStylesFromSettings(state.settings))
        const fields = getState().draft.fields
        if (
          fields.paymentReferenceType &&
          [paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(fields.paymentReferenceType.value) &&
          (!fields.referenceNumber || !fields.referenceNumber.value)
        ) {
          dispatch(
            updateReferenceNumber(
              fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
                ? generateReferenceNumber(fields.invoiceNumber.value)
                : generateStructuredCommunication(),
            ),
          )
        }
      }),
    )
    await navigateToEditor(dispatch, getState)
  }
}

interface ITitleTranslations {
  [name: string]: string
}

const hasCustomTitle = (titleTranslations: ITitleTranslations, title: string): boolean => {
  return Object.values(titleTranslations).every((t) => t.toLowerCase() !== title.toLowerCase())
}

export const initDraft = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    await loadVitalsForEditor(dispatch, getState())

    if (getState().company.agerasNextMigration) {
      await dispatch(getExternalVATRates())
    }
    const generatedDraft = draft.id
      ? await api.getDraft(draft.document, draft.id)
      : defaultDocument(draft.document, getState().settings, getState().token, getState().company, getState())
    const { settings, config } = getState()
    if (draft.receiver) {
      const allCustomers = Immutable.copy(getState().customers.collection)
      generatedDraft.receiver = allCustomers.find((c) => c.id === draft.receiver.id)
      generatedDraft.fields.customer.value = '' + draft.receiver.id
      generatedDraft.fields.locale.value = getLocale(config, draft.receiver.language, draft.receiver.address.country)
      generatedDraft.fields.timeZone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
      generatedDraft.fields.timeZone.visible = true
      generatedDraft.fields.timeZone.name = 'timeZone'
      const modelType =
        generatedDraft.document === DocumentTypes.ESTIMATE ? DocumentTypes.ESTIMATE : DocumentTypes.INVOICE
      const settingsModel = settings.models.find((m) => m.type === modelType)
      const titleField = settingsModel.fields.find((f) => f.name === 'title')
      const settingsTitle = (titleField && titleField.default) || ''
      const titleTranslations =
        modelType === DocumentTypes.ESTIMATE ? config.offerTranslations : config.invoiceTranslations
      const isCustomTitle = hasCustomTitle(titleTranslations, settingsTitle)
      const docTitle = isCustomTitle ? settingsTitle : titleTranslations[generatedDraft.fields.locale.value]
      generatedDraft.fields.title.value = docTitle?.toUpperCase()
      if (generatedDraft.fields.paymentTime) {
        generatedDraft.fields.paymentTime.value = draft.receiver.paymentTerm
      }
    }

    if (draft.fields && draft.fields.recurringProfileEnabled && draft.fields.recurringProfileEnabled.value) {
      generatedDraft.fields.recurringProfileEnabled = {
        value: true,
        name: 'recurringProfileEnabled',
        visible: true,
      }
      const nowDate = new Date()
      await dispatch(updateRecurringProfileSettings(getDefaultRecurringProfile(nowDate.toISOString())))
    }

    await dispatch(getExperiments())
    await dispatch(getCustomSettings())

    await dispatch(changeDraft(generatedDraft))
    await dispatch(
      changeFields({
        ...getState().draft.fields,
        issueDate: { ...getState().draft.fields.issueDate, value: new Date().toISOString() },
      }),
    )

    let baseNumber: number =
      draft.document !== DocumentTypes.ESTIMATE
        ? getState().company.baseInvoiceNumber
        : getState().company.baseOfferNumber
    if (!baseNumber) {
      baseNumber = 0
    }

    await dispatch(
      changeFields({
        ...getState().draft.fields,
        invoiceNumber: { ...getState().draft.fields.invoiceNumber, visible: true, value: baseNumber.toString() },
      }),
    )
    await dispatch(DocumentNumberActions.getInvoiceNumberSequences())

    if (draft.document === DocumentTypes.INVOICE) {
      await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
    } else {
      const data = await getNextNumber(draft.document)
      const invoiceNumber = data.invoicenumber === false ? 1 : String(data.invoicenumber)

      await dispatch(
        changeFields({
          ...getState().draft.fields,
          invoiceNumber: { ...getState().draft.fields.invoiceNumber, visible: true, value: invoiceNumber },
        }),
      )
    }

    const state = getState()
    const fields = state.draft.fields
    if (
      draft.document === DocumentTypes.INVOICE &&
      fields.paymentReferenceType &&
      [paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(fields.paymentReferenceType.value) &&
      (!fields.referenceNumber || !fields.referenceNumber.value)
    ) {
      dispatch(
        updateReferenceNumber(
          fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
            ? generateReferenceNumber(fields.invoiceNumber.value)
            : generateStructuredCommunication(),
        ),
      )
    }
    await dispatch(changeDraft({ ...getState().draft, isDirty: false }))
  }
}

export const navigateToEditor = async (dispatch, getState) => {
  const nav = {
    path: '/reactInvoice',
    app: {
      mod: 'reactInvoice',
    },
    origin: getState().navigation.path,
  }
  await dispatch(setNavigation(nav))
}

export const navigateToPreview = () => {
  return async (dispatch, getState) => {
    let origin = '/reactInvoice'
    if (getState().navigation.path !== '/reactInvoice') {
      origin = getState().navigation.path
    }
    const nav = {
      path: '/DraftPreview',
      app: {
        mod: 'DraftPreview',
      },
      origin,
    }
    await dispatch(setNavigation(nav))
  }
}

export const newDraft = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    navigateToEditor(dispatch, getState)
    dispatch(
      loadingEditorVitals(async (dispatch) => {
        await dispatch(initDraft(draft))
      }),
    )
  }
}

export const createDraftBasedOnApprovedInvoice = (draft: DraftInterface) => {
  return async (dispatch, getState) => {
    const newDraft = Immutable.copy(draft)
    newDraft.id = 0

    const now = dayjs()
    const then = calculateDaysFromStartDate(now, newDraft.fields.paymentTime.value)
    newDraft.fields.issueDate.value = now.toISOString()
    newDraft.fields.dueDate.value = then.toISOString()
    newDraft.fields.timeZone.value = Intl.DateTimeFormat().resolvedOptions().timeZone

    await dispatch(changeDraft(newDraft))

    let baseNumber: number = getState().company.baseInvoiceNumber
    if (!baseNumber) {
      baseNumber = 0
    }

    await dispatch(
      changeFields({
        ...getState().draft.fields,
        invoiceNumber: { ...getState().draft.fields.invoiceNumber, visible: true, value: baseNumber.toString() },
      }),
    )
    await dispatch(DocumentNumberActions.getInvoiceNumberSequences())
    await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))

    await navigateToEditor(dispatch, getState)
  }
}

export function loadDraftCopy(id: number): AsyncAction<DraftInterface> {
  return actionCreatorAsync<DraftInterface>(DraftActions.COPY_DRAFT, async () =>
    api.getDraft(DocumentTypes.INVOICE, id),
  )
}

export const copyDraft = (id: number) => {
  return async (dispatch, getState) => {
    await navigateToEditor(dispatch, getState)
    const { invoiceNumbers, settings } = getState()
    dispatch(
      loadingEditorVitals(async (dispatch) => {
        await loadVitalsForEditor(dispatch, getState())
        await dispatch(loadDraftCopy(id))
        await dispatch(DocumentNumberActions.getInvoiceNumberSequences())
        await dispatch(updateInvoiceNumber(invoiceNumbers.active))
        await dispatch(updatePaymentReferenceFromSettings(settings))
        dispatch(updateCustomerNumberFromSettings(settings))
        dispatch(updateDraftStylesFromSettings(settings))
      }),
    )
  }
}

export const deleteDraft = (id: number, type: DocumentTypes) => {
  return async (dispatch, getState) => {
    await api.deleteDraft(type, id)
    const capitalizedType = type[0].toUpperCase() + type.slice(1)
    const path = `/react${capitalizedType}List`
    dispatch(setNavigationPath(path))
  }
}

export const getDocumentPreview = (draft: DraftInterface): AsyncAction<string[]> => {
  return actionCreatorAsync<string[]>(DraftActions.PREVIEW_DOCUMENT, async () =>
    api.getHtmlPreview(draft.document, draft),
  )
}

export const getDraftPreview = (draft: DraftInterface): AsyncAction<string[]> => {
  return actionCreatorAsync<string[]>(
    DraftActions.PREVIEW_DRAFT,
    async () => api.getHtmlPreview(draft.document, draft),
    (error: AxiosError) => {
      if (error?.response?.status === 422) {
        const isNegativeGrossTotalError = error?.response?.data?.message?.includes('negative grossTotal')
        const errorTranslationKey = isNegativeGrossTotalError ? 'invoice.error_message.negative_sum' : ''
        notifier(errorTranslationKey, 'warning')
      }
      throw error
    },
  )
}

export const previewDraftFromLegacy = (id: number, type: DocumentType) => {
  return async (dispatch) => {
    if (type === DocumentTypes.INVOICE) {
      await dispatch(previewDraft(id))
    } else if (type === DocumentTypes.ESTIMATE) {
      await dispatch(previewEstimateDraft(id))
    }
  }
}

export const getDraftForPreview = (draft: DraftInterface, settings: SettingsInterface) => {
  draft = Immutable.copy(draft)
  const iban = draft.paymentMeans.find(({ type }) => type === 'IBAN')
  const settingsDefaultIBAN = settings.values.paymentMethods.find(({ type }) => type === 'IBAN')
  if (hasMinimumInfo(draft) && iban) {
    const settingsIBANs = getSettingsIbans(settings)
    const matchingIbanFromSettings = settingsIBANs.find((sIBAN) => {
      return iban.fields && sIBAN.iban === iban.fields.iban
    })
    if (!matchingIbanFromSettings && settingsDefaultIBAN) {
      const IBANIndex = draft.paymentMeans.findIndex(({ type }) => type === 'IBAN')
      if (IBANIndex >= 0) {
        iban.fields = settingsDefaultIBAN.fields
      } else {
        draft.paymentMeans.push(settingsDefaultIBAN)
      }
    }
  }
  getCustomerName(draft)
  return draft
}

export const previewDraft = (id?: number, source?: string) => {
  return async (dispatch, getState) => {
    if (getState().navigation.path !== '/reactInvoice' && id) {
      await loadVitalsForEditor(dispatch, getState())
      await dispatch(loadDraft(id))
      await dispatch(DocumentNumberActions.getInvoiceNumberSequences())
      await dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
    }
    let draft = Immutable.copy(getState().draft)

    setTypeForInvoiceDraft(draft, getState().config, getCountry(getState()))
    const shouldUpdateReferenceNumber = draft.fields.referenceNumber?.visible && source === 'invoices'
    if (
      draft.fields.paymentReferenceType &&
      [paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(draft.fields.paymentReferenceType.value) &&
      shouldUpdateReferenceNumber
    ) {
      const refNumber =
        draft.fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
          ? generateReferenceNumber(draft.fields.invoiceNumber.value)
          : generateStructuredCommunication()
      draft.fields.referenceNumber = {
        name: 'referenceNumber',
        value: refNumber,
      }
      dispatch(updateReferenceNumber(refNumber))
    }
    const characterLimits = {
      description: draft.fields?.description?.value?.length > 1024,
      textFields: draft.lines.some((l) => l.type === 'description' && l.eInvoiceLimit === true),
    } as CharacterLimitInterface
    dispatch(updateCharacterLimit(characterLimits))
    draft = handleFieldsBeforeSave(draft, getState().settings)
    await dispatch(getDraftPreview(getDraftForPreview(draft, getState().settings)))
    dispatch(navigateToPreview())
  }
}

export const updateDraftForCreditNote = (
  draft: DraftInterface,
  invoiceId: number,
  config: ConfigInterface,
  settings: SettingsInterface,
  company: CompanyInterface,
  token?: TokenInterface,
) => {
  const draftCopy = Immutable.copy(draft)
  draftCopy.document = DocumentTypes.CREDITNOTE
  draftCopy.fields.approvedInvoiceId = {
    name: 'approvedInvoiceId',
    value: invoiceId,
  }
  draftCopy.fields.issueDate.value = moment().toISOString()
  draftCopy.fields.dueDate.value = moment().toISOString()
  draftCopy.paymentMeans = draftCopy.paymentMeans.filter((p) => p.type === PaymentMeans.BANKGIRO)
  // For FI, SE, AX use the templates with OneP in name
  draftCopy.fields.template.value = handleTemplateName(draftCopy.fields.template.value, token.country)
  if (draftCopy.fields.legalterms) {
    draftCopy.fields.legalterms.visible = false
  }

  draftCopy.lines.forEach((line) => {
    if (InvoiceType.isInvoiceLine(line)) {
      line.quantity = -line.quantity
      line.defField = line.defField === defFields.TOTAL ? line.defField : defFields.PRICE
    }
  })

  delete draftCopy.fields.recurringProfileEnabled
  draftCopy.fields.title.value = config.creditTranslations[draftCopy?.fields?.locale?.value]
  draftCopy.fields.footerText.value = getFieldDefaultValue('footerText', settings, 'creditnote') as string
  draft.fields.description.value = getFieldDefaultValue('description', settings, 'creditnote') as string
  delete draft.fields.paymentTime
  delete draft.fields.approvedEstimateId
  let baseNumber: number = company.baseInvoiceNumber
  if (!baseNumber) {
    baseNumber = 0
  }
  draftCopy.fields.invoiceNumber = {
    name: 'invoiceNumber',
    visible: true,
    value: baseNumber.toString(),
  }

  return draftCopy
}

export const showDraft = (id: number, documentType: DocumentTypes) => {
  return async (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowDocumentModal',
        props: {
          content: getDraftDocumentHTML(documentType, id),
          onClose: () => dispatch(navigateRemoveModal('ShowDocumentModal')),
        },
      }),
    )
  }
}

export const openDownpaymentModal = () => {
  return async (dispatch, getState) => {
    dispatch(
      navigateAddModal({
        component: 'DownpaymentModal',
        props: {
          close: () => dispatch(navigateRemoveModal('DownpaymentModal')),
          save: (order: OrderDraftInterface) => {
            dispatch(updateOrder(order))
            dispatch(navigateRemoveModal('DownpaymentModal'))
          },
        },
      }),
    )
  }
}

export const openPaymentScheduleModal = () => {
  return async (dispatch, getState) => {
    dispatch(
      navigateAddModal({
        component: 'PaymentScheduleModal',
        props: {
          close: () => dispatch(navigateRemoveModal('PaymentScheduleModal')),
          save: (order: OrderDraftInterface) => {
            dispatch(updateOrder(order))
            dispatch(navigateRemoveModal('PaymentScheduleModal'))
          },
        },
      }),
    )
  }
}

export const checkSubscriptionAndOpenDownpaymentOrScheduleModal = (dispatch, getState, openModal) => {
  const openSubscriptionModal = () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          origin: PremiumModalOrigin.DownpaymentOrScheduleModal,
          hideDowngradePlans: true,
          next: () => dispatch(openModal()),
        },
      }),
    )
  const openIntermediary = () => {
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.PAYMENT_SCHEDULE,
          next: openSubscriptionModal,
        },
      }),
    )
  }
  const subscribed = doesCurrentPlanSupportFeature(
    getCompanySubscriptionSelector(getState()),
    QuotaTypes.PAYMENT_SCHEDULE,
  )
  if (subscribed) {
    dispatch(openModal())
  } else {
    openIntermediary()
  }
}

export const checkSubscriptionAndOpenDownpaymentModal = () => {
  return async (dispatch, getState) => {
    checkSubscriptionAndOpenDownpaymentOrScheduleModal(dispatch, getState, openDownpaymentModal)
  }
}

export const checkSubscriptionAndOpenPaymentScheduleModal = () => {
  return async (dispatch, getState) => {
    checkSubscriptionAndOpenDownpaymentOrScheduleModal(dispatch, getState, openPaymentScheduleModal)
  }
}

export const checkRecurringProfileIntermediaryModal = (dispatch) => {
  const openSubscriptionModal = () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumPlanModal',
        props: {
          origin: PremiumModalOrigin.RecurringProfile,
          hideDowngradePlans: true,
        },
      }),
    )
  const openIntermediary = () =>
    dispatch(
      navigateAddModal({
        component: 'PremiumFeatureIntermediaryModal',
        props: {
          isOpen: true,
          featureType: PremiumFeatureType.RECURRING_PROFILE,
          next: openSubscriptionModal,
        },
      }),
    )
  openIntermediary()
}

export const showRecurringProfileIntermediaryModal = () => {
  return async (dispatch) => {
    checkRecurringProfileIntermediaryModal(dispatch)
  }
}
